import { Grid } from "@mui/material";
import { useContactSubmissionProjectParams } from "app/frontend/components/contact/useContactSubmissionProjectParams";
import BaseEligibilityAccordion from "app/frontend/components/eligibility/accordions/BaseEligibilityAccordion";
import { DEFAULT_ROW, DEFAULT_VALUES } from "app/frontend/components/eligibility/accordions/project_size/constants";
import { useValidatePlug } from "app/frontend/components/eligibility/accordions/project_size/hooks/useValidatePlug";
import ProjectSizeAccordionContent from "app/frontend/components/eligibility/accordions/project_size/ProjectSizeAccordionContent";
import { IProjectSizeFormData } from "app/frontend/components/eligibility/accordions/project_size/types";
import { IEligibilityProps } from "app/frontend/components/eligibility/accordions/types";
import { EEligibilityStep } from "app/frontend/components/eligibility/constants";
import { useOnContinue } from "app/frontend/components/eligibility/hooks/useOnContinue";
import { getKwFromPlug } from "app/frontend/components/eligibility/utils";
import QuestionTooltip from "app/frontend/components/QuestionTooltip";
import { EProjectTypes } from "app/frontend/constants/enums";
import { FormContext } from "app/frontend/contexts/FormContext";
import { useParamsProjectType } from "app/frontend/hooks/project/useParamsProjectType";
import { useIsMHD } from "app/frontend/hooks/useIsMHD";
import { useProjectParams } from "app/frontend/hooks/usePropsParams";
import { useCallback, useMemo } from "react";
import { useInertiaForm } from "use-inertia-form";

const UNKNOWN_PLUG_TYPE = "UNKNOWN";

const ProjectSizeAccordion: React.FC<IEligibilityProps> = () => {
    const contactParams = useContactSubmissionProjectParams();
    const projectParams = useProjectParams();
    const defaultValues = useMemo(
        () => ({
            isProjectSizeUnknown: DEFAULT_VALUES.isProjectSizeUnknown,
            plugs:
                contactParams?.project?.plugs?.map((p, i) => ({
                    count: Number(p.count) || DEFAULT_ROW.count,
                    plug_type: p.plug_type || DEFAULT_ROW.plug_type,
                    amount_kw: p.amount_kw || DEFAULT_ROW.amount_kw,
                    index: i,
                })) || DEFAULT_VALUES.plugs,
        }),
        [contactParams],
    );

    const form = useInertiaForm<IProjectSizeFormData>(defaultValues);
    const { data, setError } = form;

    const projecType = useParamsProjectType() || contactParams?.project_type;
    const isMHD = useIsMHD();
    const onContinue = useOnContinue();

    const handleContinue = useCallback(() => {
        if (!data.isProjectSizeUnknown) {
            let error = false;
            for (let i = 0; i < data.plugs.length; i++) {
                const plug = data.plugs[i];
                if (!plug.count) {
                    setError(`plugs[${i}].count`, "Please enter a number of plugs.");
                    error = true;
                }
                if (isMHD && !plug.plug_type) {
                    setError(`plugs[${i}].plug_type`, "Please select a plug type.");
                    error = true;
                }
            }
            if (error) return;
        }

        const newProjectParams = data.isProjectSizeUnknown
            ? {
                  plugs: [
                      {
                          count: 0,
                          plug_type: UNKNOWN_PLUG_TYPE,
                          amount_kw: 0,
                      },
                  ],
              }
            : {
                  plugs: data.plugs.map((plug) => ({
                      count: Number(plug.count),
                      plug_type: plug.plug_type,
                      amount_kw: plug.amount_kw,
                  })),
              };

        onContinue({ newProjectParams });
    }, [data.isProjectSizeUnknown, data.plugs, isMHD, onContinue, setError]);

    const { h2, h4 } = useMemo(() => {
        switch (projecType) {
            case EProjectTypes.MHD: {
                return {
                    h2: "Estimate the Size of Your Charging Project",
                    h4: "Let us know your project scale to guide your incentive calculation.",
                };
            }
            default: {
                return {
                    h2: (
                        <>
                            How Many Plugs Are You Installing?{" "}
                            <QuestionTooltip
                                input={{
                                    tooltip: "These can include L2, DCFC, Micromobility, or a combination of plugs.",
                                }}
                                sx={{ fontSize: "18px" }}
                            />
                        </>
                    ),
                    h4: "This will help us determine which program is best for you.",
                };
            }
        }
    }, [projecType]);

    const isValidPlug = useValidatePlug();

    const isContinueDisabled = useMemo(() => {
        if (data.isProjectSizeUnknown) {
            return false;
        }

        return !data.plugs.every(isValidPlug);
    }, [data.isProjectSizeUnknown, data.plugs, isValidPlug]);

    const header = useMemo(() => {
        if (projectParams.plugs?.length) {
            if (projectParams.plugs.some((plug) => plug.plug_type === UNKNOWN_PLUG_TYPE)) {
                return "Confirming Project Size";
            } else {
                const countKw = projectParams.plugs.reduce((sum, plug) => sum + getKwFromPlug(plug), 0) ?? 0;
                return `${countKw.toFixed(0)} kW of Total Charging Power`;
            }
        } else {
            return "Project Size";
        }
    }, [projectParams.plugs]);

    return (
        <BaseEligibilityAccordion
            header={header}
            h2={h2}
            h4={h4}
            onContinue={handleContinue}
            hideBackButton={!isMHD}
            step={EEligibilityStep.PROJECT_SIZE}
            isContinueDisabled={isContinueDisabled}
        >
            <Grid container spacing={0} sx={{ mt: 2 }}>
                <Grid size={{ xs: 0, md: 1, lg: 2 }}>&nbsp;</Grid>
                <Grid size={{ xs: 12, md: 10, lg: 8 }}>
                    <FormContext.Provider value={form}>
                        <ProjectSizeAccordionContent />
                    </FormContext.Provider>
                </Grid>
                <Grid size={{ xs: 0, md: 1, lg: 2 }}>&nbsp;</Grid>
            </Grid>
        </BaseEligibilityAccordion>
    );
};

export default ProjectSizeAccordion;
